import { useEditorParseData } from 'components/FormulaEditor';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimContractOtherCostUpdateInput, ConstructionTypeData } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimUpdateWorkOrderChangeOtherGql, bimWorkOrderChangeOtherDataGql } from '../workOrderChangeEditQueries';

const getOtherRequestId = 'bimWorkOrderChangeOtherData';
const getUpdateOtherRequestId = 'bimWorkOrderChangeUpdateOther';

type TEditProps = {
    isOpenModal: boolean;
    refreshTableData: () => void;
    workOrderChangeOtherPriceId: number;
    workOrderOtherPriceId: number;
    constructionTypes: ConstructionTypeData[];
};

export const useEditOtherData = (props: TEditProps) => {
    const { workOrderChangeOtherPriceId, workOrderOtherPriceId, refreshTableData, isOpenModal, constructionTypes } =
        props;

    const dispatch = useDispatch();

    const [resetData] = useResetStoreData({
        requestId: getOtherRequestId,
    });
    const [resetUpdateData] = useResetStoreData({
        requestId: getUpdateOtherRequestId,
    });

    const resetModalData = useCallback(() => {
        resetUpdateData(undefined);
        resetData(undefined);
    }, [resetData, resetUpdateData]);

    const onSuccessUpdate = useCallback(() => {
        resetModalData();
        refreshTableData();
    }, [refreshTableData, resetModalData]);

    const getOtherVariables = useMemo(
        () => ({
            workOrderChangeOtherPriceId,
            workOrderOtherPriceId,
        }),
        [workOrderChangeOtherPriceId, workOrderOtherPriceId],
    );

    const [data, status] = useRequest({
        query: bimWorkOrderChangeOtherDataGql,
        requestId: getOtherRequestId,
        variables: getOtherVariables,
        withEffect: Boolean(isOpenModal && workOrderChangeOtherPriceId),
    });

    const [, updateStatus, , updateRequest] = useRequest({
        query: bimUpdateWorkOrderChangeOtherGql,
        requestId: getUpdateOtherRequestId,
        withEffect: false,
        dependenciesUpdater: onSuccessUpdate,
    });

    const otherData = useMemo(() => data?.bimWorkOrderChangeOtherData?.data, [data?.bimWorkOrderChangeOtherData?.data]);

    const { serializeStringForBackend } = useEditorParseData({});

    const onSubmit = useCallback(
        (formData: {
            calculatingTypeValue: number | null;
            formula: string | undefined;
            name: string;
            rationale: string;
            otherCostLimits: { [key: string]: boolean | string | number }[];
            otherCostType: any;
        }) => {
            if (!otherData) {
                return;
            }

            const transformOtherCostLimits = formData.otherCostLimits
                .filter((el, index: number) => el[`checkbox_${constructionTypes?.[index]?.id}`])
                .map((limit: Record<string, boolean | string | number>) =>
                    Object.entries(limit).reduce(
                        (
                            acc: {
                                constructionTypeId: number;
                                cost: number;
                            },
                            [key, value],
                        ) => {
                            if (value) {
                                if (key.includes('checkbox')) {
                                    acc = { ...acc, constructionTypeId: Number(key.split('_')[1]) };
                                }

                                if (key.includes('cost') && (typeof value === 'string' || typeof value === 'number')) {
                                    acc = { ...acc, cost: replacerCost(value) };
                                }
                            }

                            return acc;
                        },
                        {
                            constructionTypeId: 0,
                            cost: 0,
                        },
                    ),
                );

            const input: BimContractOtherCostUpdateInput = {
                calculatingType: otherData?.calculatingType,
                calculatingTypeValue: replacerCost(formData?.calculatingTypeValue),
                contractId: otherData?.contractId,
                formula: serializeStringForBackend(formData?.formula ?? ''),
                id: otherData?.id,
                name: formData.name,
                otherCostLimits: transformOtherCostLimits,
                rationale: formData.rationale,
                section: otherData?.section,
                catalogOtherCostTypeId: formData?.otherCostType?.id,
            };

            dispatch(updateRequest({ workOrderChangeOtherPriceId, input }));
        },
        [constructionTypes, dispatch, otherData, serializeStringForBackend, updateRequest, workOrderChangeOtherPriceId],
    );

    const isDataLoading = useMemo(() => R.isNil(status) || status === RequestStatus.FETCHING, [status]);
    const isUpdateInProgress = useMemo(() => updateStatus === RequestStatus.FETCHING, [updateStatus]);

    return { otherData, isDataLoading, isUpdateInProgress, resetModalData, onSubmit };
};
